import * as React from 'react';
import { cn } from '../../lib/utils';

export interface CommandHeaderCount {
  label: string;
  value: number | string;
  onSelect?: () => void;
  testId?: string;
}

export interface CommandHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Live counts for the collection below. Zero-valued entries are dropped. */
  counts?: CommandHeaderCount[];
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

/**
 * The header for a working surface: what you're looking at, how much of it
 * there is, and what you can do.
 *
 * The counts replace a row of view tabs. Tabs and filters were two ways to
 * narrow the same list, which split the state between them — the counts say
 * how the collection breaks down, and the filter builder below is the single
 * way to act on that.
 *
 * Counts of zero are dropped rather than shown as "0", so the row describes
 * what exists instead of listing what doesn't.
 */
export function CommandHeader({
  title,
  subtitle,
  counts,
  actions,
  children,
  className,
}: CommandHeaderProps) {
  const visibleCounts = (counts ?? []).filter(
    (count) => count.value !== 0 && count.value !== '0',
  );

  return (
    <div className={cn('border-b border-border bg-card px-6 pt-5', className)}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {visibleCounts.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
          {visibleCounts.map((count) => {
            const body = (
              <>
                <span className="block text-xs text-muted-foreground">{count.label}</span>
                {/* Plain, whatever the count means. Colouring the numbers made
                    the header a traffic light competing with the rows below;
                    what needs attention is said once, in the top bar. */}
                <span className="block text-[17px] font-semibold tabular-nums text-foreground">
                  {count.value}
                </span>
              </>
            );

            return count.onSelect ? (
              <button
                key={count.label}
                type="button"
                data-testid={count.testId}
                onClick={count.onSelect}
                className="rounded-md text-left transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {body}
              </button>
            ) : (
              <div key={count.label} data-testid={count.testId} className="text-left">
                {body}
              </div>
            );
          })}
        </div>
      )}

      {children && <div className="mt-4 pb-4">{children}</div>}
      {!children && <div className="pb-5" />}
    </div>
  );
}
